import { EmptyState } from '@dev-spendesk/grapes';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useState } from 'react';

import { QuerySuspense } from 'common/components/QuerySuspense';
import { useTranslation } from 'common/hooks/useTranslation';
import { useNotifications } from 'modules/app/notifications';
import { useCostCentersQuery } from 'modules/budgets/apis';
import { useQueryStates } from 'src/core/api/hooks/useQueryStates';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { useAnalyticalFieldMapping } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';
import { type AnalyticalFieldValueMappingV2 } from 'src/core/modules/bookkeep';
import { useDesignatedCustomFieldQuery } from 'src/core/modules/custom-fields/hooks/useDesignatedCustomFieldQuery';

import { useEditEcQuery } from './hooks/useEditEcQuery';
import { EcFormModal } from '../../components/EcFormModal';
import { type FormValues } from '../../components/EcFormModal/formValues';
import { type ExpenseCategory } from '../../expenseCategory';
import { useExpenseCategoriesQuery } from '../EcContainer/hooks/useExpenseCategoriesQuery';

type Props = {
  expenseCategory: ExpenseCategory;
  isOpen: boolean;
  isSplittable: boolean;
  onComplete(): void;
  onCancel(): void;
};

export const EcEditFormModalContainer = ({
  expenseCategory,
  isOpen,
  isSplittable,
  onComplete,
  onCancel,
}: Props) => {
  const { t } = useTranslation('global');
  const { dangerNotif, successNotif } = useNotifications();
  const [editExpenseCategory] = useEditEcQuery(expenseCategory.id);

  const designatedCustomFieldQueryState = useDesignatedCustomFieldQuery();
  const designatedCustomFieldId = unwrapQuery(
    designatedCustomFieldQueryState,
  )?.customFieldId;

  const queryStates = useQueryStates({
    states: {
      costCenters: useCostCentersQuery(),
      expenseCategories: useExpenseCategoriesQuery(),
    },
    reshapeData({ costCenters, expenseCategories }) {
      return {
        costCenters,
        expenseCategories,
      };
    },
  });

  const [valueMapping, setValueMapping] =
    useState<AnalyticalFieldValueMappingV2>();

  const {
    analyticalFieldMappings,
    fieldMapping,
    setFieldMapping,
    onSaveChanges,
    handleValueMapping,
    syncMode,
  } = useAnalyticalFieldMapping({
    field: {
      id: designatedCustomFieldId as string,
      name: t(
        'accounting-integration.exportSettings.fieldTypes.expenseCategory',
      ),
    },
    fieldType: 'expenseCategory',
  });

  useEffect(() => {
    const value = fieldMapping?.values.find(
      (fieldValue) => fieldValue.internalId === expenseCategory.id,
    );
    setValueMapping(value);
  }, [isOpen]);

  const shouldUpdateExpenseCategory = (values: FormValues) => {
    return (
      values.name !== expenseCategory.name ||
      !isEqual(values.costCenterIds, expenseCategory.costCenterIds)
    );
  };

  const handleCancel = () => {
    setValueMapping(undefined);
    onCancel();
  };

  const handleSubmit = async (values: FormValues) => {
    if (shouldUpdateExpenseCategory(values)) {
      await editExpenseCategory({
        name: values.name,
        costCenterIds: values.costCenterIds,
      });
    }

    if (fieldMapping) {
      handleValueMapping({
        value: valueMapping,
        internalId: expenseCategory.id,
      });
    }
  };

  return (
    <QuerySuspense
      queryState={queryStates}
      fallback={() => (
        <EmptyState
          title={t('misc.errors.networkError')}
          subtitle=""
          iconVariant="alert"
          iconName="hexagone-cross"
        />
      )}
      loading={null}
    >
      {({ costCenters, expenseCategories }) => (
        <EcFormModal
          expenseCategory={expenseCategory}
          costCenters={costCenters}
          expenseCategoryNames={expenseCategories
            .map((ec) => ec.name)
            .filter((ecName) => ecName !== expenseCategory.name)}
          isOpen={isOpen}
          onSubmit={async (values: FormValues) => {
            try {
              await handleSubmit(values);
              successNotif(t('expenseCategories.form.editSuccess'));
              onComplete();
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error(e);
              dangerNotif(t('expenseCategories.form.editError'));
            }
          }}
          onCancel={handleCancel}
          isMapSyncEnabled={false}
          isSplittable={isSplittable}
          analyticalFieldMappings={analyticalFieldMappings}
          fieldMapping={fieldMapping}
          setFieldMapping={setFieldMapping}
          onSaveChanges={onSaveChanges}
          valueMapping={valueMapping}
          setValueMapping={setValueMapping}
          initialSyncMode={syncMode}
        />
      )}
    </QuerySuspense>
  );
};
